import type { SessionEvent } from "./eventSchema";

type Subscriber = (event: SessionEvent) => void;

/**
 * spec §7.1 EventBus — DOM / storage / fetch 非依存の同期 pub/sub。
 *
 * subscriber の throw は他の subscriber と upstream (emit の呼び出し元) を
 * ブロックしない (CLAUDE.md 原則 5 fire-and-forget)。SessionEventBuffer が
 * buffer 満杯や送信失敗で throw しても、emit を呼んだ audio pipeline / VAD /
 * WebSocket handler の実行フローは止まらない。
 */
export class EventBus {
  private readonly subscribers = new Set<Subscriber>();

  emit(event: SessionEvent): void {
    for (const fn of this.subscribers) {
      try {
        fn(event);
      } catch (e) {
        // 隔離: 1 subscriber の throw は他 subscriber と upstream 呼び出し元を止めない
        console.warn("[eventBus] subscriber threw:", e);
      }
    }
  }

  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn);
    return () => {
      this.subscribers.delete(fn);
    };
  }
}

/**
 * SessionEventBuffer / emit 挿入点 (Task 6-8 で mediaStreamManager /
 * vadController / silenceTimer / streamingScribeClient / useContinuousMode /
 * ChatScreen が import する) が参照する module-level singleton。
 */
export const eventBus = new EventBus();
